import { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Popconfirm, Space, Spin, Upload, message } from "antd";
import { apiFetch } from "../../services/apiClient";

type Firma = { id: string; nombre: string; creado_en: string };
type Respuesta<T> = { success: boolean; data?: T };
const ruta = "/contratos/firmas-guardadas";

/** Biblioteca privada de la cuenta. Elegir una imagen no firma ni acepta el documento. */
export default function SelectorFirmasGuardadas({ firmaActual, onSelect }: { firmaActual?: string | null; onSelect: (png: string) => void }) {
  const [firmas, setFirmas] = useState<Firma[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [nombre, setNombre] = useState("");
  const [archivo, setArchivo] = useState<string | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const secuenciaLectura = useRef(0);
  const [origen, setOrigen] = useState<"archivo" | "dibujo">("archivo");

  const cargar = async () => {
    setCargando(true);
    const r = await apiFetch<Respuesta<Firma[]>>(ruta, { errorMsg: "No se pudieron cargar tus firmas" });
    if (r?.success && Array.isArray(r.data)) { setFirmas(r.data); setError(false); }
    else setError(true);
    setCargando(false);
  };
  useEffect(() => { void cargar(); }, []);

  const guardar = async () => {
    const imagen = origen === "dibujo" ? firmaActual : archivo;
    if (!imagen || !nombre.trim()) { message.warning("Elige un PNG o dibuja una firma y asígnale un nombre."); return; }
    setOcupado(true);
    const r = await apiFetch<Respuesta<Firma>>(ruta, { method: "POST", body: JSON.stringify({ nombre: nombre.trim(), imagen }), errorMsg: "No se pudo guardar la firma" });
    setOcupado(false);
    if (r?.success) { setNombre(""); setArchivo(null); await cargar(); }
  };

  const seleccionar = async (id: string) => {
    setOcupado(true);
    const r = await apiFetch<Respuesta<Firma & { imagen_png: string }>>(`${ruta}/${encodeURIComponent(id)}`, { errorMsg: "No se pudo cargar esa firma" });
    setOcupado(false);
    if (r?.success && r.data?.imagen_png) onSelect(r.data.imagen_png);
  };

  const eliminar = async (id: string) => {
    setOcupado(true);
    const r = await apiFetch<Respuesta<never>>(`${ruta}/${encodeURIComponent(id)}`, { method: "DELETE", errorMsg: "No se pudo eliminar la firma" });
    setOcupado(false);
    if (r?.success) await cargar();
  };

  return <section aria-label="Mis firmas guardadas">
    <p><strong>Mis firmas guardadas</strong></p>
    <p>Son privadas de tu cuenta. Elegir una imagen no firma el contrato: debes revisarlo y aceptar expresamente cada versión.</p>
    {cargando ? <Spin aria-label="Cargando firmas" /> : error ? <Alert type="error" showIcon message="No se pudieron cargar tus firmas" action={<Button onClick={() => void cargar()}>Reintentar</Button>} /> :
      firmas.length ? <Space direction="vertical" style={{ width: "100%" }}>{firmas.map((f) => <Space wrap key={f.id}>
        <span>{f.nombre}</span><Button disabled={ocupado} onClick={() => void seleccionar(f.id)}>Usar firma</Button>
        <Popconfirm title="¿Eliminar esta firma guardada?" description="No cambia los contratos ya firmados." okText="Eliminar" cancelText="Cancelar" onConfirm={() => eliminar(f.id)}><Button danger disabled={ocupado}>Eliminar</Button></Popconfirm>
      </Space>)}</Space> : <p>Aún no tienes firmas guardadas.</p>}
    <Space wrap style={{ marginTop: 12 }}>
      <Input aria-label="Nombre de la firma guardada" placeholder="Nombre de la firma" maxLength={80} value={nombre} onChange={(e) => setNombre(e.target.value)} style={{ width: 200 }} />
      <Upload accept="image/png,.png" showUploadList={false} beforeUpload={(file) => {
        const secuencia = ++secuenciaLectura.current;
        setArchivo(null);
        setOrigen("archivo");
        setLeyendo(false);
        if ((file.type && !["image/png", "application/octet-stream"].includes(file.type)) || !/\.png$/i.test(file.name) || file.size > 500_000) { message.error("Selecciona un PNG de hasta 500 KB."); return false; }
        setLeyendo(true);
        const lector = new FileReader();
        lector.onload = () => { if (secuencia === secuenciaLectura.current) { if (typeof lector.result === "string" && lector.result.includes(",")) setArchivo(`data:image/png;base64,${lector.result.slice(lector.result.indexOf(",") + 1)}`); setLeyendo(false); } };
        lector.onerror = () => { if (secuencia === secuenciaLectura.current) { setLeyendo(false); message.error("No se pudo leer el archivo PNG."); } };
        lector.readAsDataURL(file);
        return false;
      }}><Button disabled={ocupado}>Elegir PNG</Button></Upload>
      <Button disabled={ocupado || leyendo || !archivo} onClick={() => { if (archivo) onSelect(archivo); }}>Usar este PNG</Button>
      {firmaActual && <Button disabled={ocupado} type={origen === "dibujo" ? "primary" : "default"} onClick={() => setOrigen("dibujo")}>Guardar firma dibujada</Button>}
      <Button disabled={ocupado || leyendo || firmas.length >= 5 || !nombre.trim() || !(origen === "dibujo" ? firmaActual : archivo)} onClick={() => void guardar()}>Guardar en mi cuenta</Button>
    </Space>
    {origen === "archivo" && archivo && <div><img src={archivo} alt="Vista previa de firma PNG elegida" style={{ maxWidth: 240, maxHeight: 100, objectFit: "contain" }} /></div>}
    {origen === "dibujo" && firmaActual && <p>Se guardará la firma dibujada actualmente.</p>}
  </section>;
}
